const DISMISSED_VERSION_KEY = "starmesh.desktopUpdate.dismissedVersion";

/**
 * True when the renderer is running inside the Electron shell.
 *
 * @example
 * isTaskMantisDesktop();
 * // true in the packaged/dev Electron app, false in the browser
 */
export const isTaskMantisDesktop = () =>
  Boolean(typeof window !== "undefined" && window.starmeshDesktop?.isElectron);

/** How long "Later" / dismiss hides the auto prompt for the same version. */
export const DESKTOP_UPDATE_SNOOZE_MS = 24 * 60 * 60 * 1000;

/**
 * Snooze the auto-prompt for this version (not forever: a dismissed or
 * half-finished install must prompt again on a later launch).
 *
 * @param {string} version
 * @param {number} [now]
 * @example
 * rememberDismissedDesktopUpdate("0.2.0");
 */
export const rememberDismissedDesktopUpdate = (version, now = Date.now()) => {
  const value = String(version || "").trim();
  if (!value) return;
  try {
    localStorage.setItem(
      DISMISSED_VERSION_KEY,
      JSON.stringify({ version: value, at: now })
    );
  } catch (_) {
    // Private mode / blocked storage — skip snooze.
  }
};

/**
 * True when the auto-prompt for this latest version is currently snoozed.
 * A legacy plain-string value (old builds stored just the version, forever)
 * is ignored so those testers get prompted again.
 *
 * @param {string} version
 * @param {number} [now]
 * @returns {boolean}
 * @example
 * rememberDismissedDesktopUpdate("0.2.0");
 * shouldSkipAutoDesktopUpdatePrompt("0.2.0");
 * // true (for the next 24 hours)
 */
export const shouldSkipAutoDesktopUpdatePrompt = (version, now = Date.now()) => {
  const value = String(version || "").trim();
  if (!value) return false;
  try {
    const raw = localStorage.getItem(DISMISSED_VERSION_KEY);
    if (!raw) return false;
    const stored = JSON.parse(raw);
    if (!stored || stored.version !== value) return false;
    return now - Number(stored.at || 0) < DESKTOP_UPDATE_SNOOZE_MS;
  } catch (_) {
    return false;
  }
};

/**
 * Copy + actions for the desktop update popup.
 *
 * @param {object} payload
 * @returns {{ title: string, description: string, primaryLabel: string, showInstall: boolean }}
 *
 * @example
 * getDesktopUpdateDialogCopy({ status: "available", currentVersion: "0.1.0", latestVersion: "0.2.0" });
 * // { title: "Update available", showInstall: true, primaryLabel: "Install", ... }
 */
export const getDesktopUpdateDialogCopy = (payload) => {
  const status = String(payload?.status || "");
  const currentVersion = String(payload?.currentVersion || "").trim();
  const latestVersion = String(payload?.latestVersion || "").trim();
  const notes = String(payload?.notes || "").trim();
  const error = String(payload?.error || "").trim();

  if (status === "checking") {
    return {
      title: "Checking for updates",
      description: currentVersion
        ? `Looking for a newer installer than ${currentVersion}…`
        : "Looking for a newer installer…",
      primaryLabel: "OK",
      showInstall: false,
    };
  }
  if (status === "installing") {
    const percent = Number(payload?.progressPercent);
    const progressLine =
      Number.isFinite(percent) && percent >= 0 ? ` (${Math.min(percent, 100)}%)` : "";
    return {
      title: `Downloading update${progressLine}`,
      description: latestVersion
        ? `Downloading Task Mantis ${latestVersion} (about 100 MB). The installer opens when it finishes and this app closes.`
        : "Downloading the latest Task Mantis installer (about 100 MB). It opens when it finishes and this app closes.",
      primaryLabel: "OK",
      showInstall: false,
    };
  }
  if (status === "install-ready") {
    return {
      title: "Installer opened",
      description:
        "The installer is open. Drag Task Mantis onto Applications and choose Replace when macOS asks (the old copy is closing so it can be replaced), then reopen the app.",
      primaryLabel: "OK",
      showInstall: false,
    };
  }
  if (status === "available") {
    const versionLine = latestVersion
      ? `Task Mantis ${latestVersion} is ready.`
      : "A newer Task Mantis installer is ready.";
    const currentLine = currentVersion ? ` You're on ${currentVersion}.` : "";
    const notesLine = notes ? ` ${notes}` : "";
    return {
      title: "Update available",
      description: `${versionLine}${currentLine}${notesLine}`.trim(),
      primaryLabel: "Install",
      showInstall: true,
    };
  }
  if (status === "current") {
    return {
      title: "You're up to date",
      description: currentVersion
        ? `Task Mantis ${currentVersion} is the latest version.`
        : "You already have the latest Task Mantis installer.",
      primaryLabel: "OK",
      showInstall: false,
    };
  }
  return {
    title: "Couldn't check for updates",
    description:
      error || "Try again in a bit, or download the latest installer from the website.",
    primaryLabel: "OK",
    showInstall: false,
  };
};
